/**
 * Headless "index grid" shell over the EditableTable primitive — the
 * browsing pattern extracted from ExpensesEditableTable: toolbar search
 * over the caller's COMPLETE row set, CONTROLLED column sort applied to
 * the full filtered set, a render-only row cap with a "Show all (N)"
 * affordance, and (optionally) row expansion with the standard chevron
 * action cell.
 *
 * The cap is a RENDER cap applied inside the primitive (`maxRenderedRows`),
 * after its column-filter stage — the FULL search-filtered, sorted set is
 * passed as `data`, so filter dropdown options and filtering see every
 * row, and sorting a column yields the global order, not the first-cap's.
 * The primitive reports the post-column-filter row count back
 * (`onVisibleRowCountChange`), which drives "Show all (N)" (rendered by
 * `IndexGridSearchControls`).
 *
 * Rows are caller-ordered (e.g. date-desc) and can come from ANY list
 * source — the shell has zero coupling to how the rows were fetched.
 */
import {
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { ChevronRight, type LucideIcon } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import { IndexGridCapFooter } from './IndexGridCapFooter';
import { IndexGridEmptyState } from './IndexGridEmptyState';
import { sortRowsByColumn } from './utils';
import type { ColumnConfig, ColumnSortState } from './types';

/** Default render cap — see `rowCap` below. */
const DEFAULT_ROW_CAP = 200;

/**
 * Copy/action overrides for the grid's zero-row state. Every field is
 * optional — the defaults are deliberately entity-agnostic ("Nothing here yet"
 * / "No matches") so a grid gets a correct empty state with no configuration.
 */
export interface IndexGridEmptyStateOptions {
  /** Icon for the no-data-at-all case. Default `Inbox`. */
  icon?: LucideIcon;
  /** Heading for the no-data-at-all case. */
  title?: ReactNode;
  /** Explanation for the no-data-at-all case — say what the list is for. */
  description?: ReactNode;
  /**
   * Create affordance (typically a `<Button>`) for the no-data-at-all case.
   * Never rendered for the no-matches case.
   */
  action?: ReactNode;
  /** Heading when the search / filters matched nothing. */
  noMatchTitle?: ReactNode;
  /** Explanation when the search / filters matched nothing. */
  noMatchDescription?: ReactNode;
  /** Icon when the search / filters matched nothing. Default `SearchX`. */
  noMatchIcon?: LucideIcon;
}

export interface UseIndexGridShellOptions<Row extends Record<string, unknown>> {
  /**
   * The complete row set, in the caller's order (e.g. date-desc). Any list
   * source works — the shell never touches how the rows were fetched.
   * Rows MUST expose a string `id` field: the shell keys expansion, sorting
   * and `tableProps.rowIdField` on `'id'` (thread a rowIdField option only
   * when a real consumer needs a different key).
   */
  rows: Row[];
  /** The grid's columns — the controlled sort resolves its comparator here. */
  columns: ColumnConfig[];
  /**
   * Per-row search haystack: return the field values the toolbar search
   * should match. Non-string entries (null/undefined/numbers) are ignored.
   */
  searchHaystack: (row: Row) => ReadonlyArray<string | null | undefined>;
  /** Render cap handed to the primitive as `maxRenderedRows`. Default 200. */
  rowCap?: number;
  /**
   * Enable row expansion. Default false — omits ALL expansion wiring from
   * `tableProps` (no action cell, no expandedRows, no onRowExpand); the
   * caller adds `expandedRowRender` itself when expandable.
   */
  expandable?: boolean;
  /**
   * Start every row expanded while the COMPLETE row set (pre-search) has at
   * most this many rows — a one- or two-row list then reads as detail rather
   * than a near-empty grid. The user's own toggles always win. Counting
   * `rows`, not the searched set, keeps rows from springing open as the user
   * types. Only meaningful with `expandable`. Default 2; pass 0 to disable.
   */
  autoExpandUpTo?: number;
  /**
   * Rows that start expanded on their FIRST render — derived from the data
   * (e.g. "the best match when it is strong"), so the grid never paints a row
   * collapsed and then opens it a frame later (a visible flicker / layout
   * shift). Layered like `autoExpandUpTo`: the user's own toggles always win.
   * Only meaningful with `expandable`.
   */
  defaultExpanded?: (row: Row) => boolean;
  /** Chevron aria-label when collapsed. Default `spreadsheet:table.expandRow`. */
  expandLabel?: string;
  /** Chevron aria-label when expanded. Default `spreadsheet:table.collapseRow`. */
  collapseLabel?: string;
  /** Passed through to the primitive (post-filter, PRE-cap ids). */
  onVisibleRowIdsChange?: (ids: string[]) => void;
  /**
   * Window-snap floating header — pins a display-only header mirror to the top
   * of the app content area while the grid scrolls past. **Default true**: index
   * grids are full-page, page-scrolled lists where this is wanted. Pass `false`
   * for a bounded/embedded host (e.g. a grid inside a card with its own scroll).
   */
  floatingHeader?: boolean;
  /**
   * Copy/action overrides for the zero-row state. Omit entirely and the grid
   * still gets the generic empty state — this is only for saying something
   * more useful ("No expenses yet — record your first one") or handing the
   * user a create button.
   */
  emptyState?: IndexGridEmptyStateOptions;
  /**
   * Set while the caller's list query is still in flight. Suppresses the
   * zero-row empty state so a loading grid never asserts "nothing here yet"
   * before the rows land. Default false — hosts that already gate the whole
   * grid behind a loading skeleton (expenses, bills, funds, transactions) do
   * not need it; hosts that render the grid during load should pass it.
   */
  isLoading?: boolean;
  /**
   * Set when the HOST has already narrowed `rows` with a filter of its own
   * (the places advanced-filter panel, the funds show-inactive toggle, the
   * transactions suspense deep-link). Such a host shrinks `rows` before the
   * shell ever sees them, so the zero-row state can't tell "your filter
   * matched nothing" from "there is nothing here" and wrongly reads as the
   * latter. ORed into the same discriminator as the primitive's own column
   * filters. Default false.
   *
   * A plain boolean on purpose: an object would have to churn `tableProps`
   * identity on every host render.
   */
  hasUpstreamFilters?: boolean;
}

/** The props the shell wires into `<EditableTable {...shell.tableProps} />`. */
export interface IndexGridShellTableProps<Row extends Record<string, unknown>> {
  /** The FULL search-filtered, sorted set — never pre-sliced. */
  data: Row[];
  rowIdField: string;
  showSaveButtons: false;
  sort: ColumnSortState | null;
  onSortChange: (next: ColumnSortState | null) => void;
  maxRenderedRows: number | undefined;
  onVisibleRowCountChange: (count: number) => void;
  onVisibleRowIdsChange?: (ids: string[]) => void;
  renderCapFooter: (visibleRowCount: number) => ReactNode;
  /** Omitted while `isLoading` — the primitive then renders no empty state. */
  renderEmptyState?: (ctx: { hasColumnFilters: boolean }) => ReactNode;
  renderActionCell?: (row: Record<string, unknown>) => ReactNode;
  canExpand?: (row: Record<string, unknown>) => boolean;
  expandedRows?: Set<string>;
  onRowExpand?: (rowId: string) => void;
  floatingHeader: boolean;
  /** Always true — index grids are read-only display lists. */
  displayOnly: true;
}

export interface IndexGridShell<Row extends Record<string, unknown>> {
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  showAll: boolean;
  setShowAll: (showAll: boolean) => void;
  /** Post-column-filter count reported back by the primitive. */
  visibleRowCount: number;
  rowCap: number;
  sort: ColumnSortState | null;
  expandedRows: Set<string>;
  toggleExpanded: (rowKey: string) => void;
  /**
   * Open a row without toggling — for programmatic opens (deep links), which
   * must not collapse a row that `autoExpandUpTo` already opened.
   */
  expandRow: (rowKey: string) => void;
  /** The FULL search-filtered, sorted set (also `tableProps.data`). */
  sortedRows: Row[];
  tableProps: IndexGridShellTableProps<Row>;
}

export function useIndexGridShell<Row extends Record<string, unknown>>(
  options: UseIndexGridShellOptions<Row>,
): IndexGridShell<Row> {
  const {
    rows,
    columns,
    searchHaystack,
    rowCap = DEFAULT_ROW_CAP,
    expandable = false,
    autoExpandUpTo = 2,
    defaultExpanded,
    expandLabel,
    collapseLabel,
    onVisibleRowIdsChange,
    floatingHeader = true,
    emptyState,
    isLoading = false,
    hasUpstreamFilters = false,
  } = options;
  const { t } = useTranslation('spreadsheet');

  // Expansion = the user's explicit per-row choices layered over a default:
  // open while the complete list is short (`autoExpandUpTo`), closed otherwise.
  const autoExpand = expandable && rows.length > 0 && rows.length <= autoExpandUpTo;
  // The ids open by default: every row while the list is short, plus the
  // caller's data-derived picks.
  const defaultOpenIds = useMemo(() => {
    const open = new Set<string>();
    if (!expandable) return open;
    for (const row of rows) {
      if (autoExpand || defaultExpanded?.(row)) open.add(row.id as string);
    }
    return open;
  }, [expandable, autoExpand, rows, defaultExpanded]);
  const defaultOpenRef = useRef(defaultOpenIds);
  defaultOpenRef.current = defaultOpenIds;
  const [expandOverrides, setExpandOverrides] = useState<ReadonlyMap<string, boolean>>(new Map());
  const toggleExpanded = useCallback((rowKey: string) => {
    setExpandOverrides((prev) =>
      new Map(prev).set(rowKey, !(prev.get(rowKey) ?? defaultOpenRef.current.has(rowKey))),
    );
  }, []);
  const expandRow = useCallback((rowKey: string) => {
    setExpandOverrides((prev) => (prev.get(rowKey) === true ? prev : new Map(prev).set(rowKey, true)));
  }, []);
  const expandedRows = useMemo(() => {
    const open = new Set<string>();
    for (const id of defaultOpenIds) {
      if (expandOverrides.get(id) !== false) open.add(id);
    }
    for (const [id, isOpen] of expandOverrides) if (isOpen) open.add(id);
    return open;
  }, [defaultOpenIds, expandOverrides]);

  const [showAll, setShowAll] = useState(false);
  // Post-column-filter row count reported by the primitive — the honest
  // basis for the "Show all (N)" gate/count (unlike `sortedRows.length`,
  // it reflects active per-column filters).
  const [visibleRowCount, setVisibleRowCount] = useState(0);
  // Controlled sort — the primitive renders the header affordances; we own
  // the ordering so it applies to the FULL row set before the render cap.
  const [sort, setSort] = useState<ColumnSortState | null>(null);

  // Toolbar search — caller-owned per the primitive's contract; runs over
  // the COMPLETE row set via the caller's haystack.
  const [searchTerm, setSearchTerm] = useState('');
  const deferredSearch = useDeferredValue(searchTerm);
  const filteredRows = useMemo(() => {
    const q = deferredSearch.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((row) => {
      const haystack = searchHaystack(row)
        .filter((v): v is string => typeof v === 'string')
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [rows, deferredSearch, searchHaystack]);

  // "Show all" is per-result-set: a NEW search means a new result set, so
  // the render cap re-applies. Keyed on the deferred term only — never on
  // visibleRowCount, which would fight ordinary refetches.
  useEffect(() => {
    setShowAll(false);
  }, [deferredSearch]);

  // ---- Sort (full set) -----------------------------------------------------
  // The shared comparator (the same one the primitive uses uncontrolled)
  // sorts ALL filtered rows. The FULL sorted set is passed as `data` — the
  // render cap is the primitive's (`maxRenderedRows`, applied after its
  // column-filter stage), so a sorted column surfaces the true global
  // extremes and filter options include beyond-cap values.
  const sortedRows = useMemo(() => {
    if (!sort) return filteredRows;
    const col = columns.find((c) => c.data === sort.col);
    if (!col) return filteredRows;
    return sortRowsByColumn(filteredRows, col, sort.direction, { rowIdField: 'id' });
  }, [filteredRows, sort, columns]);

  // ---- Expansion action cell -------------------------------------------------
  // Per docs/EXPANDED_ROWS.md: the leading-column affordance is a pure expand
  // toggle (same look as the primitive's own expand-toggle-btn); entity
  // actions live in the expanded panel, not a row menu.
  const canExpand = useCallback(() => true, []);

  const expandedRowsRef = useRef(expandedRows);
  expandedRowsRef.current = expandedRows;

  const resolvedExpandLabel = expandLabel ?? t('table.expandRow');
  const resolvedCollapseLabel = collapseLabel ?? t('table.collapseRow');

  const renderActionCell = useCallback(
    (row: Record<string, unknown>) => {
      const rowKey = row.id as string;
      const isExpanded = expandedRowsRef.current.has(rowKey);
      return (
        <button
          type="button"
          className="rollback-btn expand-toggle-btn"
          aria-expanded={isExpanded}
          aria-controls={`${rowKey}-detail`}
          aria-label={isExpanded ? resolvedCollapseLabel : resolvedExpandLabel}
          onClick={(e) => {
            e.stopPropagation();
            toggleExpanded(rowKey);
          }}
        >
          <ChevronRight
            className="w-3.5 h-3.5"
            style={{
              transform: isExpanded ? 'rotate(90deg)' : undefined,
              transition: 'transform 0.15s',
            }}
          />
        </button>
      );
    },
    [resolvedExpandLabel, resolvedCollapseLabel, toggleExpanded],
  );

  // ---- Zero-row empty state --------------------------------------------------
  // The shell CAN tell the two zero cases apart: `rows` is the caller's
  // COMPLETE pre-search set, so a zero-row render while `rows.length > 0` can
  // only be the toolbar search (or a column filter) hiding everything, and
  // `rows.length === 0` is a genuinely empty list. The primitive's own
  // `hasColumnFilters` is ORed in so a filter-only wipe still reads as "no
  // matches" even if the caller pre-sliced its rows.
  //
  // A host that filters BEFORE the shell (the places advanced-filter panel,
  // the funds show-inactive toggle, the transactions suspense deep-link)
  // shrinks `rows` itself, so that shape would read as "nothing here yet"
  // when a host filter is what hid the data — such hosts pass
  // `hasUpstreamFilters` and get the "no matches" copy instead.
  //
  // Overrides come through a ref: consumers pass an object literal, and
  // depending on its identity would rebuild `tableProps` — and therefore
  // re-render the whole grid — on every host render. Read at the primitive's
  // render time, the same pattern `renderActionCell` uses above.
  const emptyStateRef = useRef(emptyState);
  emptyStateRef.current = emptyState;
  const hasRows = rows.length > 0;

  const renderEmptyState = useCallback(
    ({ hasColumnFilters }: { hasColumnFilters: boolean }) => {
      const filtered = hasRows || hasColumnFilters || hasUpstreamFilters;
      const overrides = emptyStateRef.current;
      return (
        <IndexGridEmptyState
          filtered={filtered}
          icon={filtered ? overrides?.noMatchIcon : overrides?.icon}
          title={filtered ? overrides?.noMatchTitle : overrides?.title}
          description={filtered ? overrides?.noMatchDescription : overrides?.description}
          action={overrides?.action}
        />
      );
    },
    [hasRows, hasUpstreamFilters],
  );

  // ---- Table props -----------------------------------------------------------
  const tableProps = useMemo<IndexGridShellTableProps<Row>>(() => {
    const base: IndexGridShellTableProps<Row> = {
      // NEVER pre-sliced — the primitive's column-filter options and
      // filtering run on `data`; the cap is purely its render cap.
      data: sortedRows,
      rowIdField: 'id',
      showSaveButtons: false,
      sort,
      onSortChange: setSort,
      maxRenderedRows: showAll ? undefined : rowCap,
      onVisibleRowCountChange: setVisibleRowCount,
      onVisibleRowIdsChange,
      // The primitive renders this only while the cap is truncating; `count`
      // is its live post-filter total, so the footer's "of N" tracks filters.
      renderCapFooter: (count: number) => (
        <IndexGridCapFooter total={count} shown={rowCap} onShowAll={() => setShowAll(true)} />
      ),
      // Withheld while the list query is in flight — a loading grid must not
      // claim the list is empty.
      renderEmptyState: isLoading ? undefined : renderEmptyState,
      floatingHeader,
      // Index grids are read-only lists: render cells as normal display
      // cells, not with the muted locked-cell tint (editable-table.css).
      displayOnly: true,
    };
    if (!expandable) return base;
    return {
      ...base,
      renderActionCell,
      canExpand,
      expandedRows,
      onRowExpand: toggleExpanded,
    };
  }, [
    sortedRows,
    sort,
    showAll,
    rowCap,
    onVisibleRowIdsChange,
    expandable,
    renderActionCell,
    canExpand,
    expandedRows,
    toggleExpanded,
    floatingHeader,
    isLoading,
    renderEmptyState,
  ]);

  return {
    searchTerm,
    setSearchTerm,
    showAll,
    setShowAll,
    visibleRowCount,
    rowCap,
    sort,
    expandedRows,
    toggleExpanded,
    expandRow,
    sortedRows,
    tableProps,
  };
}
